import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useDashboardPermissions } from '@/hooks/useDashboardPermissions';
import { usePendingApprovalsCount } from '@/hooks/usePendingApprovalsCount';
import { useUnreadMessagesCount } from '@/hooks/useUnreadMessagesCount';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ClipboardCheck, MessageSquare } from 'lucide-react';

function Counter({ n }: { n: number }) {
  if (!n) return null;
  return (
    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
      {n > 99 ? '99+' : n}
    </span>
  );
}

const ROLE_LABELS: Record<string, string> = {
  superadmin: 'Super Admin',
  agency_admin: 'Agency Admin',
  agent: 'Agent',
  developer: 'Developer',
  sales_agent: 'Sales Agent',
  sales_team_leader: 'Sales Team Leader',
  sales_manager: 'Sales Manager',
  marketing_agent: 'Marketing Agent',
  marketing_team_leader: 'Marketing Team Leader',
  marketing_manager: 'Marketing Manager',
  it_staff: 'IT Staff',
  accounting_staff: 'Accounting',
  hr_staff: 'HR Staff',
  quality_control_lead: 'Quality Control Lead',
  quality_control_agent: 'Quality Control Agent',
  ad_operations_lead: 'Ad Operations Lead',
  ad_operations_agent: 'Ad Operations Agent',
  customer_support_lead: 'Customer Support Lead',
  customer_support_agent: 'Customer Support',
  lead_ops_lead: 'Lead Ops Lead',
  lead_ops_agent: 'Lead Ops Agent',
};

export function DashboardHeader({ title = 'Dashboard' }: { title?: string }) {
  const { profile, user, roles } = useAuth();
  const { permissions: p } = useDashboardPermissions();
  const navigate = useNavigate();
  const { total: approvalsTotal, breakdown } = usePendingApprovalsCount();
  const unreadMessages = useUnreadMessagesCount();

  const displayName = profile?.full_name || user?.email?.split('@')[0] || 'User';
  const primaryRole = roles.find((r) => r !== 'user') || roles[0];
  const roleLabel = primaryRole ? (ROLE_LABELS[primaryRole] || primaryRole) : 'Member';

  const showApprovals =
    p.canApproveListings || p.canApproveApplications || p.canModerateReviews ||
    p.canHandleReports || p.canManagePackageInquiries;

  const handleApprovalsClick = () => {
    if (breakdown.listings && p.canApproveListings) navigate('/dashboard/property-approvals');
    else if (breakdown.applications && p.canApproveApplications) navigate('/dashboard/agency-approvals');
    else if (breakdown.reviews && p.canModerateReviews) navigate('/dashboard/reviews-moderation');
    else if (breakdown.reports && p.canHandleReports) navigate('/dashboard/reports');
    else if (breakdown.packageInquiries && p.canManagePackageInquiries) navigate('/dashboard/package-inquiries');
    else if (p.canApproveListings) navigate('/dashboard/property-approvals');
    else if (p.canApproveApplications) navigate('/dashboard/agency-approvals');
    else if (p.canModerateReviews) navigate('/dashboard/reviews-moderation');
    else if (p.canHandleReports) navigate('/dashboard/reports');
    else if (p.canManagePackageInquiries) navigate('/dashboard/package-inquiries');
  };

  const approvalsTooltip = [
    breakdown.listings ? `${breakdown.listings} listing(s)` : '',
    breakdown.applications ? `${breakdown.applications} application(s)` : '',
    breakdown.reviews ? `${breakdown.reviews} review(s)` : '',
    breakdown.reports ? `${breakdown.reports} report(s)` : '',
    breakdown.packageInquiries ? `${breakdown.packageInquiries} package inquiry/inquiries` : '',
  ].filter(Boolean).join(' · ') || 'No pending approvals';

  return (
    <header className="h-16 flex items-center justify-between border-b px-4 md:px-6 bg-card/80 backdrop-blur-sm sticky top-0 z-40">
      <div className="flex items-center gap-3">
        <SidebarTrigger className="mr-1" />
        <h1 className="font-display font-bold text-lg hidden sm:block">{title}</h1>
      </div>

      <TooltipProvider delayDuration={200}>
        <div className="flex items-center gap-1 md:gap-2">
          {showApprovals && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 relative text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  onClick={handleApprovalsClick}
                  aria-label="Pending approvals"
                >
                  <ClipboardCheck className="h-4 w-4" />
                  <Counter n={approvalsTotal} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Pending approvals — {approvalsTooltip}</TooltipContent>
            </Tooltip>
          )}

          {p.canViewMessages && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 relative text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  onClick={() => navigate('/dashboard/messages')}
                  aria-label="Messages"
                >
                  <MessageSquare className="h-4 w-4" />
                  <Counter n={unreadMessages} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{unreadMessages ? `${unreadMessages} unread message(s)` : 'Messages'}</TooltipContent>
            </Tooltip>
          )}

          {p.canViewNotifications && <NotificationBell />}

          <div className="flex items-center gap-2 ml-2 pl-2 border-l">
            <Avatar className="h-9 w-9">
              <AvatarImage src={profile?.avatar_url} />
              <AvatarFallback className="bg-muted text-xs">{displayName[0]?.toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="hidden lg:block text-right">
              <div className="text-sm font-semibold leading-tight">{displayName}</div>
              <div className="text-[10px] text-muted-foreground">{roleLabel}</div>
            </div>
          </div>
        </div>
      </TooltipProvider>
    </header>
  );
}
